import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import { ROOT_CLASS } from "mobile/mobileConfig";

/**
 * Generic single-choice dropdown.
 *
 * phr has no UI kit and a native <select> can't carry a two-line option or a
 * disabled reason, which this form needs. Shares usePopover with StatusSelect
 * so the portal/flip/clamp rules exist once — see that file for why.
 *
 * Keyboard: ArrowDown/Up move a roving highlight, Enter/Space commit, Escape
 * closes, Home/End jump. StatusSelect has none of this; a form with six of
 * these needs it.
 *
 * `searchable` turns THE FIELD ITSELF into the search box — the trigger becomes
 * a text input you type into, filtering the list below it. It is not a second
 * search field inside the menu: that was the first shape of this and it was
 * rejected, because a box that appears at the top of a portalled menu sits
 * nowhere near the field it belongs to.
 *
 * NOT a free-text field, unlike SpecialtyPicker, which is the other combobox in
 * this repo. Every option here is an id-keyed catalogue row — an account code, a
 * plan, a provider — so a query matching nothing must never become the value.
 * The query lives only while the menu is open and the field reverts to showing
 * the committed option the moment it closes.
 */

/**
 * Cap on a searchable menu's height, in px.
 *
 * usePopover hands the menu ALL the room between the trigger and the viewport
 * edge, which for a long list means a slab spanning most of the page — a
 * fifteen-account menu opened upwards from the bottom of the create form and
 * covered the breadcrumb. Capped only for searchable menus, and only here
 * rather than in usePopover, so StatusSelect and every other menu keep the
 * placement they have. You type to narrow the list; you do not need to see
 * fifteen rows at once.
 */
const SEARCH_MENU_MAX = 320;

const norm = (s) => String(s == null ? "" : s).trim().toLowerCase();

const Select = ({
  id,
  value,
  options,
  getKey,
  getLabel,
  getHint,
  isDisabled,
  placeholder,
  disabled,
  invalid,
  // Extra class for the MENU. The menu portals to <body>, so a caller cannot
  // reach it with a descendant selector — sections that need a board-specific
  // menu skin pass a hook here instead of restyling every Select globally.
  menuClassName,
  searchable,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const close = useCallback(() => setOpen(false), []);
  const optionRefs = useRef([]);
  /* Whether what is in the box is a QUERY or the committed label. Without the
     split, opening the field would show the current choice and typing would
     have to erase it first; and a half-typed query would be left sitting in a
     field that reads back as the booking's value. */
  const typed = useRef(false);

  /* READ FROM THE UNFILTERED LIST, both of these, and it matters.
     `selected` is what the field shows when closed, which must keep being the
     current choice while a query hides it; and the disabled gate below must not
     go true because a query matched nothing — that would disable the very field
     needed to clear the query, wedging it with no way out but a reload. */
  const selectedIdx = options.findIndex((o) => getKey(o) === value);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;
  const selectedLabel = selected ? getLabel(selected) : "";

  /* Matched on the LABEL only. A hint is a secondary line — a price, a
     sub-limit — and letting a query hit it means typing "500" filters providers
     by fee, which is a different feature and a surprising one. */
  const shown = useMemo(() => {
    const q = norm(query);
    if (!searchable || !q || !typed.current) return options;
    return options.filter((o) => norm(getLabel(o)).indexOf(q) !== -1);
  }, [options, query, searchable, getLabel]);

  /* DECLARED AFTER `shown` ON PURPOSE, so its length can be handed over as the
     reflow key: filtering changes the menu's height, and a flipped menu is
     anchored by its TOP, so one placed while the list was long keeps that top
     as the list shrinks — narrowing fifteen options to one left the single
     remaining row floating far above the field it belongs to. */
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close, shown.length);

  /* Land the highlight on the current choice when the menu opens, and on the
     first match while a query is narrowing it.

     KEYED ON `open` AND `query`, NEVER ON `options`. Two of the arrays passed to
     this component are rebuilt on every render of their parent — `modes` and
     `clinics` in CreateAppointment are computed inline in the render body, not
     memoised — so anything keyed on the options array's identity would re-run
     on every keystroke and clear the query as it was typed. */
  useEffect(() => {
    if (!open) return;
    if (query) setActive(0);
    else setActive(selectedIdx >= 0 ? selectedIdx : 0);
  }, [open, query, selectedIdx]);

  /* THE REVERT, and it covers every way the menu can close — the option click,
     Escape, a click outside, a scroll outside — because all of them funnel
     through `open` going false. Clearing `typed` is what puts the committed
     label back in the field, so a query that matched nothing cannot be left on
     screen looking like a selection. */
  useEffect(() => {
    if (!open) {
      setQuery("");
      typed.current = false;
    }
  }, [open]);

  // Keep the highlight in view inside the menu's own scroll container.
  useEffect(() => {
    if (!open || active < 0) return;
    const el = optionRefs.current[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const commit = (opt) => {
    if (isDisabled && isDisabled(opt)) return;
    setOpen(false);

    /* MOBILE VIEW ONLY: PUT THE KEYBOARD AWAY. A searchable field IS the
       search box, so after a choice focus is still in a text input and the
       on-screen keyboard still covers half the sheet — with the list gone,
       there is nothing left to type into it.

       AND IT CANNOT CLOSE BY ITSELF. Tapping an option would normally blur the
       field and take the keyboard with it; the option's own
       `onMouseDown -> preventDefault` (below, on the combobox path only) stops
       exactly that, so the field keeps focus while the list is navigated. That
       is right on a desktop and is the whole reason the keyboard stays here, so
       the dismissal has to be explicit.

       DESKTOP KEEPS FOCUS: there is no keyboard to dismiss, and dropping focus
       after each choice would break tabbing through a form of these.

       ONLY WHEN `searchable`. The button variant never takes focus for typing
       and raises no keyboard, so blurring it would cost a focus ring and buy
       nothing.

       Keyed on the root class, like usePopover's own mobile branch — a shared
       component must not reach into a screen's state. */
    if (
      searchable &&
      triggerRef.current &&
      document.documentElement.classList.contains(ROOT_CLASS)
    ) {
      triggerRef.current.blur();
    }

    if (getKey(opt) !== value) onChange(getKey(opt), opt);
  };

  const step = (dir) => {
    if (!shown.length) return;
    let i = active;
    // Skip disabled options rather than landing on something uncommittable.
    for (let n = 0; n < shown.length; n += 1) {
      i = (i + dir + shown.length) % shown.length;
      if (!isDisabled || !isDisabled(shown[i])) break;
    }
    setActive(i);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      step(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(shown.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (shown[active]) commit(shown[active]);
    }
  };

  /* The combobox keyboard, which is deliberately NOT the button's.
     SPACE and HOME/END are missing on purpose: on a text input they are a
     space character and caret movement. The button handler commits on Space and
     jumps the highlight on Home/End, which on a typable field would close the
     menu mid-word and stop the caret ever reaching the start of a query. */
  const onComboKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else step(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open) step(-1);
    } else if (e.key === "Enter") {
      if (open && shown[active]) {
        e.preventDefault();
        commit(shown[active]);
      }
    } else if (e.key === "Escape" && open) {
      /* Stopped here as well as handled: this field can sit inside a dialog
         that also closes on Escape, and dismissing the menu should not also
         dismiss the form around it. */
      e.stopPropagation();
      setOpen(false);
    }
  };

  const menuId = id ? `${id}-menu` : undefined;

  return (
    <div className={`zy-select${searchable ? " zy-select-combo" : ""}`}>
      {searchable ? (
        <>
          <input
            id={id}
            ref={triggerRef}
            type="text"
            className={`zy-select-combo-input${invalid ? " is-invalid" : ""}`}
            disabled={disabled || !options.length}
            /* The committed label whenever the box is not being typed into, so
               the field always reads back what will be booked. */
            value={open && typed.current ? query : selectedLabel}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={menuId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && shown[active] && id ? `${id}-opt-${active}` : undefined
            }
            /* CLICK, NOT FOCUS. usePopover dismisses on any scroll outside the
               menu, and focusing a field that is not fully in view makes the
               browser scroll it there — so opening on focus can open the list
               and close it in the same tick. SpecialtyPicker hit exactly this.
               Tabbing through the form no longer pops a list open either, which
               is the better behaviour anyway. */
            onClick={() => setOpen(true)}
            onChange={(e) => {
              typed.current = true;
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={onComboKeyDown}
          />
          {/* A SIBLING, because an <input> cannot hold children the way the
              button held its caret. `pointer-events: none` in the stylesheet
              keeps a click on it landing in the field. */}
          <span className="zy-select-caret" aria-hidden="true">
            &#9662;
          </span>
        </>
      ) : (
        <button
          id={id}
          ref={triggerRef}
          type="button"
          className={`zy-select-trigger${invalid ? " is-invalid" : ""}${
            selected ? "" : " is-placeholder"
          }`}
          disabled={disabled || !options.length}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onKeyDown}
        >
          <span className="zy-select-value">
            {selected ? getLabel(selected) : placeholder}
          </span>
          <span className="zy-select-caret" aria-hidden="true">
            &#9662;
          </span>
        </button>
      )}

      {open &&
        ReactDOM.createPortal(
          <ul
            id={searchable ? menuId : undefined}
            className={`zy-select-menu${menuClassName ? ` ${menuClassName}` : ""}`}
            role="listbox"
            ref={menuRef}
            style={
              searchable
                ? {
                    ...menuStyle,
                    maxHeight: Math.min(menuStyle.maxHeight || SEARCH_MENU_MAX, SEARCH_MENU_MAX),
                  }
                : menuStyle
            }
          >
            {searchable && shown.length === 0 && (
              <li className="zy-select-empty">No match for &ldquo;{query.trim()}&rdquo;</li>
            )}

            {shown.map((opt, i) => {
              const k = getKey(opt);
              const off = isDisabled ? isDisabled(opt) : false;
              return (
                <li key={k}>
                  <button
                    type="button"
                    role="option"
                    /* Only for the combobox, which points
                       aria-activedescendant at it. Absent otherwise, so the
                       non-searchable markup is unchanged. */
                    id={searchable && id ? `${id}-opt-${i}` : undefined}
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    aria-selected={k === value}
                    disabled={!!off}
                    className={`zy-select-option${k === value ? " is-selected" : ""}${
                      i === active ? " is-active" : ""
                    }${off ? " is-disabled" : ""}`}
                    /* The field keeps focus while the list is navigated, so the
                       click must not steal it before onClick runs. */
                    onMouseDown={searchable ? (e) => e.preventDefault() : undefined}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => commit(opt)}
                  >
                    <span className="zy-select-option-label">{getLabel(opt)}</span>
                    {getHint && getHint(opt) ? (
                      <span className="zy-select-option-hint">{getHint(opt)}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

Select.propTypes = {
  id: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array.isRequired,
  getKey: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  /** Secondary line under an option (e.g. a price). */
  getHint: PropTypes.func,
  /** Renders the option greyed and uncommittable. */
  isDisabled: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  /** Extra class on the portalled menu, for section-specific skins. */
  menuClassName: PropTypes.string,
  /** Makes the FIELD the search box: type in it to filter the list. */
  searchable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

Select.defaultProps = { placeholder: "Select", searchable: false };

export default Select;
